import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface DeviceInfo {
  userAgent: string;
  language: string;
  platform: string;
  screenWidth: number;
  screenHeight: number;
  timezone: string;
}

interface AnalyticsEvent {
  user_id: string;
  session_id: string;
  event_type: string;
  event_name: string;
  page_path?: string;
  element_id?: string;
  element_text?: string;
  metadata?: Record<string, unknown>;
  device_info?: DeviceInfo;
}

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
    sessionStorage.setItem('analytics_session_start', Date.now().toString());
  }
  return sessionId;
};

// Get device info
const getDeviceInfo = (): DeviceInfo => ({
  userAgent: navigator.userAgent,
  language: navigator.language,
  platform: navigator.platform,
  screenWidth: window.screen.width,
  screenHeight: window.screen.height,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

// Event buffer for batching
let eventBuffer: AnalyticsEvent[] = [];
let flushTimeout: NodeJS.Timeout | null = null;

const flushEvents = async () => {
  if (eventBuffer.length === 0) return;
  
  const eventsToSend = [...eventBuffer];
  eventBuffer = [];
  
  try {
    // Use type assertion to bypass TypeScript check since table was just created
    const { error } = await (supabase as any)
      .from('analytics_events')
      .insert(eventsToSend);
    
    if (error) {
      console.error('Failed to flush analytics events:', error);
      // Re-add failed events back to buffer (up to a limit)
      eventBuffer = [...eventsToSend.slice(-50), ...eventBuffer].slice(-100);
    }
  } catch (err) {
    console.error('Analytics flush error:', err);
  }
};

const scheduleFlush = () => {
  if (flushTimeout) return;
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushEvents();
  }, 5000); // Flush every 5 seconds
};

export const useAnalytics = () => {
  const location = useLocation();
  const lastPathRef = useRef<string>('');
  const pageStartTimeRef = useRef<number>(Date.now());

  // Track page view
  const trackPageView = useCallback(async (path: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const event: AnalyticsEvent = {
      user_id: user.id,
      session_id: getSessionId(),
      event_type: 'page_view',
      event_name: path,
      page_path: path,
      device_info: getDeviceInfo(),
      metadata: {
        referrer: document.referrer,
        title: document.title,
      },
    };

    eventBuffer.push(event);
    scheduleFlush();
  }, []);

  // Track custom event
  const trackEvent = useCallback(async (
    eventType: string,
    eventName: string,
    metadata?: Record<string, unknown>,
    elementId?: string,
    elementText?: string
  ) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const event: AnalyticsEvent = {
      user_id: user.id,
      session_id: getSessionId(),
      event_type: eventType,
      event_name: eventName,
      page_path: location.pathname,
      element_id: elementId,
      element_text: elementText,
      metadata,
      device_info: getDeviceInfo(),
    };

    eventBuffer.push(event);
    scheduleFlush();
  }, [location.pathname]);

  // Track button/link clicks
  const trackClick = useCallback((
    elementId: string,
    elementText?: string,
    metadata?: Record<string, unknown>
  ) => {
    trackEvent('click', elementId, metadata, elementId, elementText);
  }, [trackEvent]);

  // Track feature usage
  const trackFeature = useCallback((
    featureName: string,
    metadata?: Record<string, unknown>
  ) => {
    trackEvent('feature', featureName, metadata);
  }, [trackEvent]);

  // Track form events
  const trackForm = useCallback((
    formName: string,
    action: 'submit' | 'abandon' | 'start',
    metadata?: Record<string, unknown>
  ) => {
    trackEvent('form', `${formName}_${action}`, metadata);
  }, [trackEvent]);

  // Track navigation
  const trackNavigation = useCallback((
    from: string,
    to: string,
    metadata?: Record<string, unknown>
  ) => {
    trackEvent('navigation', 'route_change', { from, to, ...metadata });
  }, [trackEvent]);

  // Auto-track page views
  useEffect(() => {
    const currentPath = location.pathname;
    
    if (currentPath !== lastPathRef.current) {
      // Track time on previous page
      if (lastPathRef.current) {
        const timeOnPage = Date.now() - pageStartTimeRef.current;
        trackEvent('engagement', 'time_on_page', {
          page: lastPathRef.current,
          duration_ms: timeOnPage,
          duration_seconds: Math.round(timeOnPage / 1000),
        });
      }
      
      // Track new page view
      trackPageView(currentPath);
      lastPathRef.current = currentPath;
      pageStartTimeRef.current = Date.now();
    }
  }, [location.pathname, trackPageView, trackEvent]);

  // Track session start
  useEffect(() => {
    const sessionStart = sessionStorage.getItem('analytics_session_start');
    const isNewSession = !sessionStart || (Date.now() - parseInt(sessionStart)) > 30 * 60 * 1000; // 30 min timeout
    
    if (isNewSession) {
      sessionStorage.setItem('analytics_session_id', `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
      sessionStorage.setItem('analytics_session_start', Date.now().toString());
      trackEvent('session', 'session_start', { device_info: getDeviceInfo() });
    }

    // Flush on page unload
    const handleUnload = () => {
      trackEvent('session', 'session_end', {
        duration_ms: Date.now() - parseInt(sessionStorage.getItem('analytics_session_start') || '0'),
      });
      flushEvents();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [trackEvent]);

  return {
    trackEvent,
    trackClick,
    trackFeature,
    trackForm,
    trackNavigation,
    trackPageView,
  };
};

// Singleton for use outside of React components
export const analytics = {
  trackEvent: async (
    eventType: string,
    eventName: string,
    metadata?: Record<string, unknown>
  ) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const event: AnalyticsEvent = {
      user_id: user.id,
      session_id: getSessionId(),
      event_type: eventType,
      event_name: eventName,
      page_path: window.location.pathname,
      metadata,
      device_info: getDeviceInfo(),
    };

    eventBuffer.push(event);
    scheduleFlush();
  },
  
  trackFeature: (featureName: string, metadata?: Record<string, unknown>) => {
    analytics.trackEvent('feature', featureName, metadata);
  },
  
  trackClick: (elementId: string, metadata?: Record<string, unknown>) => {
    analytics.trackEvent('click', elementId, metadata);
  },
};
